/**
 * Pure formatting helpers for tool-call display chrome:
 *  - {@link humanizeToolName}  identifier → Title Case
 *  - {@link formatArgs}        args object → compact key=value lines
 *  - {@link formatResult}      result payload → preformatted body
 *
 * Kept dependency-free so the leaf tool-call component, tests, and
 * any future LangSmith-style introspection UI can compose them.
 */

/**
 * Convert tool identifiers from `snake_case` (Python convention) or
 * `camelCase` (TS convention) to `Title Case` for display.
 *  - `read_file` → `Read File`
 *  - `getPerformanceOverview` → `Get Performance Overview`
 *  - `tool` → `Tool`
 */
export function humanizeToolName(raw: string): string {
  if (!raw) return 'Tool';
  const withSpaces = raw
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim();
  return withSpaces
    .split(/\s+/)
    .map((w) => {
      if (!w) return '';
      return w.charAt(0).toUpperCase() + w.slice(1);
    })
    .join(' ');
}

/**
 * Render tool args as a compact key=value block, one entry per line.
 * Strings are double-quoted (truncated past 80 chars), arrays show
 * length, nested objects collapse to `{...}`.
 */
export function formatArgs(args: unknown): string {
  if (args == null) return '';
  if (typeof args === 'string') return args;
  if (typeof args !== 'object' || Array.isArray(args)) {
    return String(args);
  }
  const entries = Object.entries(args as Record<string, unknown>);
  if (entries.length === 0) return '';
  return entries.map(([k, v]) => `${k}=${formatArgValue(v)}`).join('\n');
}

function formatArgValue(v: unknown): string {
  if (v == null) return 'null';
  if (typeof v === 'string') {
    return v.length > 80 ? `"${v.slice(0, 80)}…"` : `"${v}"`;
  }
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) return `[${v.length}]`;
  if (typeof v === 'object') return '{…}';
  return String(v);
}

/**
 * Render tool results as a preformatted block.
 *  - String content → as-is
 *  - LangChain content blocks (array of `{type:'text', text}`) → joined text
 *  - Other shapes → JSON.stringify(_, null, 2)
 */
export function formatResult(result: unknown): string {
  if (result == null) return '';
  if (typeof result === 'string') return result;
  if (Array.isArray(result)) {
    const joined = result
      .map((b) => {
        if (
          b &&
          typeof b === 'object' &&
          typeof (b as Record<string, unknown>)['text'] === 'string'
        ) {
          return (b as Record<string, unknown>)['text'] as string;
        }
        return '';
      })
      .filter(Boolean)
      .join('');
    if (joined) return joined;
  }
  try {
    return JSON.stringify(result, null, 2);
  } catch {
    return String(result);
  }
}
